import {
  AutocompleteInput,
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  Link,
  List,
  ReferenceInput,
  useGetIdentity,
  useRecordContext,
  useRefresh,
  useUpdate,
} from 'react-admin';
import { Button, Chip, CircularProgress } from '@mui/material';

import CustomPagination from '../../CustomPagination';
import {
  UserRole,
  type PxlVisionManualVerification as PxlVisionManualVerificationType,
} from '../../@generated/schemas';
import { ResourceView } from '@ra-data-prisma/dataprovider';
import { VFC } from 'react';
import cookie from 'js-cookie';
import { gql } from '@apollo/client';
import { url } from 'src';
import { useTranslate } from '../../locales';

export const PxlVisionVerificationFilter = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const defaultUserFilter = {
    deletedAt: { equals: null },
    companyId:
      identity?.userRole && identity?.userRole !== UserRole.SuperAdmin
        ? {
            equals: identity.companyId,
          }
        : undefined,
  };

  return (
    <Filter>
      <ReferenceInput
        source="userId"
        reference="User"
        alwaysOn
        filter={defaultUserFilter}>
        <AutocompleteInput label={translate('admin.user')} optionText={(choice: any) => choice?.email} />
      </ReferenceInput>
    </Filter>
  );
};

const VerifyButton: VFC = () => {
  const translate = useTranslate();
  const record = useRecordContext<PxlVisionManualVerificationType>();
  const { identity } = useGetIdentity();
  const refreshData = useRefresh();
  const [update, { isLoading }] = useUpdate();

  const handleClick = async () => {
    await update(
      'PxlVisionManualVerification',
      {
        id: record.id,
        data: {
          verifiedByUser: record?.verifiedAt ? null : identity?.id,
          verifiedAt: record?.verifiedAt
            ? {
                set: null,
              }
            : new Date(),
        },
      },
      { returnPromise: true },
    );

    refreshData();
  };

  return (
    <Button
      variant="text"
      disabled={isLoading}
      onClick={handleClick}
      color="primary">
      {isLoading && (
        <CircularProgress style={{ height: 20, width: 20, marginRight: 4 }} />
      )}
      {record?.verifiedAt ? translate('admin.unverify') : translate('admin.verified')}
    </Button>
  );
};

const DownloadPxlVision = () => {
  const translate = useTranslate();
  const record = useRecordContext<PxlVisionManualVerificationType>();
  return (
    (record && (
      <a
        target="_blank"
        href={`${url}/admin/pxl-vision-verification/${
          record.transactionCode
        }?access_token=${cookie.get('access_token')}`}
        download>
        {translate('admin.download')}
      </a>
    )) ||
    null
  );
};

export const PxlVisionVerificationList = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const defaultFilter = {
    deletedAt: { equals: null },
    user:
      identity?.userRole && identity?.userRole !== UserRole.SuperAdmin
        ? {
            is: {
              companyId: {
                equals: identity.companyId,
              },
            },
          }
        : {},
  };

  return (
    <List
      filters={<PxlVisionVerificationFilter />}
      filter={defaultFilter}
      pagination={<CustomPagination />}>
      <Datagrid>
        <FunctionField
          label={translate('admin.user')}
          render={(row) => (
            <Link to={`/User/${row.userId}`}>{row?.user?.email}</Link>
          )}
        />
        <DateField source="createdAt" label={translate('admin.createdAt')} showTime />
        <FunctionField
          label={translate('admin.verifiedBy')}
          render={(row) => (
            <Link to={`/User/${row.verifiedByUserId}`}>
              {row?.verifiedByUser?.email}
            </Link>
          )}
        />
        <FunctionField
          label={translate('admin.type')}
          render={(row) => <Chip label={row?.type} />}
        />
        <DateField source="verifiedAt" label={translate('admin.verifiedAt')} />
        <VerifyButton />
        <DownloadPxlVision />
      </Datagrid>
    </List>
  );
};

export const PxlVisionManualVerification: ResourceView = {
  resource: 'PxlVisionManualVerification',
  fragment: {
    one: {
      type: 'document',
      mode: 'extend',
      doc: gql`
        fragment PxlVisionManualVerificationFragment on PxlVisionManualVerification {
          user {
            id
            email
          }
          verifiedByUser {
            id
            email
          }
        }
      `,
    },
    many: {
      type: 'document',
      mode: 'extend',
      doc: gql`
        fragment PxlVisionManualVerificationFragment on PxlVisionManualVerification {
          user {
            id
            email
          }
          verifiedByUser {
            id
            email
          }
        }
      `,
    },
  },
};
